(function(){
  'use strict';
  if (typeof window === 'undefined') return;

  const CLICK_SELECTORS = '.product-card, .grid-item, [data-product-id], .card-wrapper';
  const PREFERRED_3D = '[data-media-type="model"], [data-media-type="3d"], .product-model, .media-item--model, .thumb--model';
  const MODEL_VIEWER_SELECTORS = 'model-viewer, [data-model-viewer], .model-viewer, model';
  const POSTER_SELECTORS = '.deferred-media__poster-button, .poster-button, button.poster';
  const CANDIDATE_SELECTORS = ['.product__media', '.product-single__media', '.product-single', '.main-media', '.product-page'];
  const SAFETY_TIMEOUT = 1200;
  const POSTER_CLICK_DELAY = 80;
  const WAIT_FOR_MV_MS = 1600;
  const SCHEDULER_DEBOUNCE_MS = 300;
  const EXTERNAL_WAIT = 140;
  const cores = navigator.hardwareConcurrency || 2;
  const DEBOUNCE_DELAY = (cores >= 4 && !/Mobi|Android/i.test(navigator.userAgent)) ? 160 : 240;
  const PREFERS_REDUCED_MOTION = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;

  window.__glb_feature_flags = window.__glb_feature_flags || { autostart: true, telemetry: false };
  window.__glb_autostart = window.__glb_autostart || {};
  const NS = window.__glb_autostart;
  if (NS.installed || window.__glb_autostart_enabled === false) return;
  NS.installed = true;

  const _linkCache = { preloads: new Set(), preconnects: new Set() };
  const domCache = new Map();
  const sleep = ms => new Promise(r => setTimeout(r, ms));
  const dispatchTarget = document.body || document.documentElement || document;

  function scheduleIdle(fn, delay = 0){
    if (typeof window.requestIdleCallback === 'function') return window.requestIdleCallback(() => fn(), { timeout: Math.max(50, delay) });
    return setTimeout(fn, delay);
  }

  function microtask(fn){
    if (typeof queueMicrotask === 'function') return queueMicrotask(fn);
    return Promise.resolve().then(fn);
  }

  function tryParseUrl(src){
    try { return new URL(src, location.href); } catch (e) { return null; }
  }

  function queryFirst(selector){
    if (domCache.has(selector)) return domCache.get(selector);
    const el = document.querySelector(selector);
    domCache.set(selector, el);
    return el;
  }

  function queryFirstCandidate(){
    for (const s of CANDIDATE_SELECTORS){
      const el = queryFirst(s);
      if (el) return el;
    }
    return document.body;
  }

  const productRoot = queryFirstCandidate();

  function isAutostartAllowed(){
    return !!window.__glb_feature_flags.autostart && !PREFERS_REDUCED_MOTION;
  }

  function isConnectionSlow(){
    const conn = navigator.connection;
    if (!conn) return false;
    return ['2g','slow-2g','slow'].includes(conn.effectiveType);
  }

  function isLikelyBot(){
    const ua = navigator.userAgent;
    return /HeadlessChrome|PhantomJS|Googlebot|bingbot|Slurp|DuckDuckBot|Baiduspider|YandexBot|MJ12bot/i.test(ua) || navigator.webdriver;
  }

  if (isLikelyBot()) return;

  const REUSABLE_MOUSE_CLICK = (function(){
    try { return new MouseEvent('click', { bubbles: true, cancelable: true }); } catch (e) { return null; }
  })();

  function getReusableEvent(){
    try { return new CustomEvent('glb:productCardClicked', { bubbles: true }); } catch (e) {
      try { const ev = document.createEvent('Event'); ev.initEvent('glb:productCardClicked', true, true); return ev; } catch (ex) { return null; }
    }
  }

  function safeDispatchReusableEvent(){
    const ev = getReusableEvent();
    if (!ev) return false;
    try { dispatchTarget.dispatchEvent(ev); return true; } catch (e) { try { document.dispatchEvent(ev); return true; } catch (ex) { return false; } }
  }

  function dispatchProductClickEvent(productId){
    NS.last_product_click = String(productId || '');
    return Promise.resolve(safeDispatchReusableEvent());
  }

  function acquireLock(){
    if (NS._locked) return false;
    NS._locked = true;
    if (NS._safetyId) clearTimeout(NS._safetyId);
    NS._safetyId = setTimeout(() => { NS._locked = false; NS._safetyId = null; }, SAFETY_TIMEOUT);
    return true;
  }

  function releaseLock(){
    NS._locked = false;
    if (NS._safetyId) { clearTimeout(NS._safetyId); NS._safetyId = null; }
  }

  function detect3DThumb(){
    try {
      const pref = productRoot.querySelector(PREFERRED_3D);
      if (pref) return pref;
      const list = productRoot.querySelectorAll('[data-media-id]');
      for (const el of list){
        const txt = (el.innerText + ' ' + el.className).toLowerCase();
        if (/model|3d|glb|usdz|play_model/.test(txt)) return el;
      }
      return list[0] || null;
    } catch (e) { return null; }
  }

  function findModelViewerNear(node){
    return node?.querySelector?.(MODEL_VIEWER_SELECTORS) || document.querySelector(MODEL_VIEWER_SELECTORS);
  }

  function tryClick(el){
    if (!el) return false;
    try {
      if (typeof el.click === 'function') { el.click(); return true; }
      if (REUSABLE_MOUSE_CLICK && el.dispatchEvent) { el.dispatchEvent(REUSABLE_MOUSE_CLICK); return true; }
      el.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
      return true;
    } catch (e) { return false; }
  }

  function refreshLinkCache(){
    const nodes = document.getElementsByTagName('link');
    for (const l of nodes){
      if (l.rel === 'preload' || l.rel === 'modulepreload') _linkCache.preloads.add(l.href);
      else if (l.rel === 'preconnect') _linkCache.preconnects.add(l.href);
    }
  }

  function ensureLinkPreconnect(origin, crossorigin){
    if (_linkCache.preconnects.size === 0) refreshLinkCache();
    if (_linkCache.preconnects.has(origin)) return;
    const pc = document.createElement('link');
    pc.rel = 'preconnect';
    pc.href = origin;
    if (crossorigin) pc.crossOrigin = crossorigin;
    document.head.appendChild(pc);
    _linkCache.preconnects.add(origin);
  }

  function ensureLinkPreload(href, as, crossorigin, type){
    if (_linkCache.preloads.size === 0) refreshLinkCache();
    if (_linkCache.preloads.has(href)) return;
    const pl = document.createElement('link');
    pl.rel = as === 'module' ? 'modulepreload' : 'preload';
    pl.as = as || 'fetch';
    pl.href = href;
    if (crossorigin) pl.crossOrigin = crossorigin;
    if (type) pl.type = type;
    document.head.appendChild(pl);
    _linkCache.preloads.add(href);
  }

  const MODEL_SRC_ALLOWLIST = window.__glb_model_src_allowlist || [location.origin, 'https://cdn.myshopify.com'];

  function isValidModelSrc(src){
    try {
      if (!src || typeof src !== 'string') return false;
      const url = tryParseUrl(src);
      if (!url) return false;
      return /^https?:$/i.test(url.protocol) && MODEL_SRC_ALLOWLIST.includes(url.origin);
    } catch (e) { return false; }
  }

  function waitForModelViewerInsertion(thumb, timeout = WAIT_FOR_MV_MS){
    return new Promise(resolve => {
      const direct = findModelViewerNear(thumb);
      if (direct) return resolve(direct);
      let resolved = false;
      let mo = null;
      const finish = (result) => {
        if (resolved) return;
        resolved = true;
        if (mo) mo.disconnect();
        resolve(result);
      };
      mo = new MutationObserver(() => {
        const found = findModelViewerNear(thumb);
        if (found) finish(found);
      });
      mo.observe(productRoot, { childList: true, subtree: true });
      setTimeout(() => finish(findModelViewerNear(thumb)), timeout + 50);
    });
  }

  let modelViewerLoaderPromise = null;

  function ensureModelViewerScript(options = {}){
    if (modelViewerLoaderPromise) return modelViewerLoaderPromise;
    modelViewerLoaderPromise = new Promise(resolve => {
      if (document.querySelector(MODEL_VIEWER_SELECTORS)) return resolve(true);
      if (isConnectionSlow() || PREFERS_REDUCED_MOTION) return resolve(false);
      scheduleIdle(() => {
        const defaultSrc = options.src || window.__glb_model_viewer_options?.src || 'https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js';
        const defaultModuleSrc = options.srcModule || window.__glb_model_viewer_options?.srcModule || defaultSrc;
        let resolved = false;
        const onLoadResolve = () => { if (!resolved) { resolved = true; resolve(true); } };
        const onErrorResolve = () => { if (!resolved) { resolved = true; resolve(false); } };
        const supportsModule = (function(){
          try { const script = document.createElement('script'); return 'noModule' in script; } catch (e) { return false; }
        })();
        if (supportsModule) {
          try {
            ensureLinkPreload(defaultModuleSrc, 'module', options.crossorigin || 'anonymous');
            const sModule = document.createElement('script');
            sModule.type = 'module';
            sModule.src = defaultModuleSrc;
            sModule.defer = true;
            if (options.crossorigin) sModule.crossOrigin = options.crossorigin;
            if (options.integrityModule) sModule.integrity = options.integrityModule;
            sModule.onload = onLoadResolve;
            sModule.onerror = onErrorResolve;
            document.head.appendChild(sModule);
            const fallbackToNomodule = setTimeout(() => {
              if (document.querySelector(MODEL_VIEWER_SELECTORS)) return;
              try {
                const sFallback = document.createElement('script');
                sFallback.src = defaultSrc;
                sFallback.defer = true;
                sFallback.noModule = true;
                if (options.crossorigin) sFallback.crossOrigin = options.crossorigin;
                if (options.integrity) sFallback.integrity = options.integrity;
                sFallback.onload = onLoadResolve;
                sFallback.onerror = onErrorResolve;
                document.head.appendChild(sFallback);
              } catch (e) {}
            }, options.moduleFallbackTimeout || 2500);
            sModule.addEventListener('load', () => clearTimeout(fallbackToNomodule));
            sModule.addEventListener('error', () => clearTimeout(fallbackToNomodule));
            return;
          } catch (e) {}
        }
        try {
          const s = document.createElement('script');
          s.src = defaultSrc;
          s.defer = true;
          s.async = false;
          if (options.crossorigin) s.crossOrigin = options.crossorigin;
          if (options.integrity) s.integrity = options.integrity;
          s.onload = onLoadResolve;
          s.onerror = onErrorResolve;
          document.head.appendChild(s);
        } catch (e) { resolve(false); }
      }, 160);
    });
    return modelViewerLoaderPromise;
  }

  function observeThumbVisibility(thumb, onVisible, options = {}){
    if (!thumb || typeof IntersectionObserver === 'undefined') { microtask(onVisible); return null; }
    const io = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          onVisible();
        }
      }
    }, { root: options.root || null, rootMargin: options.rootMargin || '200px', threshold: options.threshold || 0.01 });
    io.observe(thumb);
    return io;
  }

  function activationFix(mv){
    if (!mv) return;
    try {
      if (mv.focus) mv.focus();
      requestAnimationFrame(() => {
        const eventType = typeof PointerEvent === 'function' ? 'pointer' : 'mouse';
        const down = eventType === 'pointer' ? new PointerEvent('pointerdown', { bubbles: true, pointerType: 'mouse' }) : new MouseEvent('mousedown', { bubbles: true });
        const up = eventType === 'pointer' ? new PointerEvent('pointerup', { bubbles: true, pointerType: 'mouse' }) : new MouseEvent('mouseup', { bubbles: true });
        mv.dispatchEvent(down);
        setTimeout(() => mv.dispatchEvent(up), 8);
      });
      scheduleIdle(() => {
        if (typeof mv.play === 'function') mv.play();
        else mv.setAttribute?.('autoplay', '');
      }, 180);
    } catch (e) {}
  }

  function shouldPreloadModel(src) {
    if (!src) return false;
    const mvOptions = window.__glb_model_viewer_options || {};
    if (mvOptions.preloadModels === false) return false;
    if (NS.last_product_click) return true;
    if (document.visibilityState === 'visible' && window.innerWidth > (mvOptions.preloadViewportWidth || 700)) return true;
    if (isConnectionSlow() || PREFERS_REDUCED_MOTION) return false;
    return false;
  }

  function reportMetric(name, value) {
    if (!window.__glb_feature_flags.telemetry) return;
    try {
      const payload = JSON.stringify({ name, value, url: location.pathname, ts: Date.now() });
      if (navigator.sendBeacon) navigator.sendBeacon('/__glb_metrics', payload);
      else fetch('/__glb_metrics', { method: 'POST', body: payload, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(()=>{});
    } catch (e) {}
  }

  function invalidateDomCache(keys) {
    try {
      if (!keys) return domCache.clear();
      for (const k of keys) domCache.delete(k);
    } catch (e) {}
  }

  try {
    const globalDomObserver = new MutationObserver((mutations) => {
      if (mutations.length > 8) return invalidateDomCache();
      for (const m of mutations) {
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          invalidateDomCache(['.product__media', '.product-single__media', '.product-single', '.main-media', '.product-page']);
          break;
        }
      }
    });
    globalDomObserver.observe(document.documentElement || document.body, { childList: true, subtree: true });
  } catch (e) {}

  async function startFlowOnce({ waitMs } = {}){
    if (!isAutostartAllowed() || isConnectionSlow()) return { ok: false };
    const thumb = detect3DThumb();
    if (!thumb) return { ok: false };
    await sleep(POSTER_CLICK_DELAY);
    const poster = thumb.querySelector?.(POSTER_SELECTORS) || queryFirst(POSTER_SELECTORS) || document.querySelector(POSTER_SELECTORS);
    if (poster) {
      const img = poster.tagName === 'IMG' ? poster : poster.querySelector('img');
      if (img && !img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
      tryClick(poster);
    }
    const mvOptions = window.__glb_model_viewer_options || {};
    let visibilityObserved = false;
    const onVisible = async () => {
      if (visibilityObserved) return;
      visibilityObserved = true;
      await ensureModelViewerScript(mvOptions).catch(() => {});
      const mv = await waitForModelViewerInsertion(thumb, Number(waitMs) || WAIT_FOR_MV_MS);
      if (!mv) return;
      const dataSrc = mv.getAttribute?.('data-src') || mv.getAttribute?.('data-model') || null;
      let finalSrc = dataSrc;
      if (!finalSrc) {
        const candidate = thumb.querySelector?.('source[type^="model/"], [data-model-src]');
        finalSrc = candidate?.getAttribute('data-model-src') || candidate?.getAttribute('src') || null;
      }
      if (finalSrc && isValidModelSrc(finalSrc)) {
        if (shouldPreloadModel(finalSrc)) {
          const parsed = tryParseUrl(finalSrc);
          if (parsed) {
            ensureLinkPreconnect(parsed.origin, mvOptions.crossorigin || 'anonymous');
            ensureLinkPreload(finalSrc, 'fetch', mvOptions.crossorigin || 'anonymous', finalSrc.includes('.glb') ? 'model/gltf-binary' : undefined);
          }
        }
        microtask(() => {
          try {
            mv.setAttribute('src', finalSrc);
            mv.setAttribute('muted', '');
            mv.setAttribute('playsinline', '');
            mv.setAttribute('autoplay', '');
            if (typeof mv.play === 'function') mv.play().catch(() => activationFix(mv));
          } catch (e) { activationFix(mv); }
        });
        reportMetric('model_load_scheduled', finalSrc);
      }
      return { ok: true, mv };
    };
    observeThumbVisibility(thumb, onVisible, { rootMargin: '300px' });
    const existingMv = findModelViewerNear(thumb);
    if (existingMv) return onVisible();
    return { ok: true };
  }

  NS.start_model_viewer = opts => startFlowOnce(opts || {});
  NS.autostart_scheduleRun = (delay = 120) => {
    const now = Date.now();
    if (now - (NS.lastRun || 0) < SCHEDULER_DEBOUNCE_MS || NS._timer) return;
    NS.lastRun = now;
    NS._timer = setTimeout(() => { NS._timer = null; startFlowOnce().catch(() => {}); }, Math.max(0, delay));
  };

  if (!NS.autoplay_listening) {
    NS._productCardHandler = () => { startFlowOnce().catch(() => {}); };
    dispatchTarget.addEventListener('glb:productCardClicked', NS._productCardHandler, { passive: true });
    NS.autoplay_listening = true;
  }

  if (!NS.click_added) {
    NS._clickHandler = event => {
      const el = event.target.closest?.(CLICK_SELECTORS);
      if (!el) return;
      acquireLock();
      const productId = el.getAttribute('data-product-id') || '';
      NS.last_product_click = productId;
      if (NS._debounceId) clearTimeout(NS._debounceId);
      NS._debounceId = setTimeout(() => {
        NS._debounceId = null;
        const external = window.__glb_autostart_externalRun;
        if (typeof external === 'function') {
          let dispatched = false;
          const mark = () => { dispatched = true; };
          dispatchTarget.addEventListener('glb:productCardClicked', mark, { once: true });
          try { external(); } catch (e) {}
          setTimeout(() => {
            if (!dispatched) dispatchProductClickEvent(productId);
            releaseLock();
          }, EXTERNAL_WAIT);
        } else {
          dispatchProductClickEvent(productId);
          releaseLock();
        }
      }, DEBOUNCE_DELAY);
    };
    const container = queryFirstCandidate() || document.body;
    container.addEventListener('click', NS._clickHandler, { passive: true });
    NS.click_added = true;
  }

  function cleanupAll(){
    try {
      dispatchTarget.removeEventListener('glb:productCardClicked', NS._productCardHandler);
      const container = queryFirstCandidate() || document.body;
      container.removeEventListener('click', NS._clickHandler, { passive: true });
      clearTimeout(NS._timer);
      clearTimeout(NS._debounceId);
      clearTimeout(NS._safetyId);
      NS.installed = NS.autoplay_listening = NS.click_added = false;
    } catch (e) {}
  }

  window.addEventListener('beforeunload', cleanupAll, { passive: true });
  window.addEventListener('pagehide', cleanupAll, { passive: true });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') cleanupAll(); }, { passive: true });

  window.__glb_start_model_viewer = NS.start_model_viewer;
  window.__glb_dispatch_product_click = (p) => {
    const id = (typeof p === 'string') ? p : p?.getAttribute?.('data-product-id') || '';
    NS.last_product_click = id;
    return dispatchProductClickEvent(id);
  };
  window.__glb_autostart_scheduleRun = NS.autostart_scheduleRun;
})();
